import { Service } from "@turizi/shared";
import { getAttrValue } from "@/lib/models-utils";
import { useTranslation } from "react-i18next";
import { SERVICES_PATH } from "@/lib/navigation";
import { wrapInLink } from "@/lib/draw-utils";
import { IconMoon, IconArrowsRightLeft, IconArrowRight, IconUsers } from "@tabler/icons-react";

interface Props {
    service: Service;
    links?: boolean;
}

export default function TransferTitle({
    service,
    links = false,
}: Props) {
    const { t } = useTranslation();

    const round_trip = getAttrValue(service, 'round_trip');
    const regular = getAttrValue(service, 'regular_schedule');
    const vehicle_type = getAttrValue(service, 'vehicle_type');
    const origin_city = getAttrValue(service, 'origin_city');
    const destination_city = getAttrValue(service, 'destination_city');
    const origin_zone = getAttrValue(service, 'origin_zone');
    const destination_zone = getAttrValue(service, 'destination_zone');
    const start_time = getAttrValue(service, 'start_time');
    const is_nightly = start_time > "18:00";

    const LocationDisplay = ({ city, zone }: { city?: string, zone?: string }) => {
        if (!city && !zone) return <span className="text-gray-400">*</span>;
        return (
            <div className="flex gap-1">
                <div className="font-normal text-gray-700">{city}</div>
                <div className="font-semibold text-gray-900">{zone}</div>
            </div>
        );
    };

    return (
        <div>
            {wrapInLink(
                <div className="flex flex-col text-left">
                    {/* Main Row */}
                    <div className="flex items-center gap-0.5 flex-wrap">
                        <LocationDisplay city={origin_city} zone={origin_zone} />
                        {round_trip ? (
                            <div className="text-gray-500 flex-shrink-0 mt-0.5 px-0.5" title={t('round_trip')}>
                                <IconArrowsRightLeft size={14} stroke={1.8} />
                            </div>
                        ) : (
                            <div className="text-gray-500 flex-shrink-0 mt-0.5 px-0.5" title={t('round_trip')}>
                                <IconArrowRight size={14} stroke={1.8} />
                            </div>
                        )}
                        <LocationDisplay city={destination_city} zone={destination_zone} />
                    </div>

                    {/* Secondary Row (Service Name) */}
                    {service.name && (
                        <div className="text-xxs mt-0.5 font-semibold text-gray-700">
                            {service.name}
                        </div>
                    )}

                    {/* Third Row (Pills) are always */}
                    <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[9px]">
                        {round_trip && (
                            <div className="flex items-center gap-1 bg-purple-700 text-purple-100 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                                {t('round_trip')}
                            </div>
                        )}

                        {vehicle_type && (
                            <div className="flex items-center gap-1 bg-gray-600 text-white px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                                {t(vehicle_type)}
                            </div>
                        )}

                        <div className={`px-2.5 py-0.5 rounded-full font-medium whitespace-nowrap 
                            ${regular
                                ? 'bg-green-50 text-green-900/70'
                                : 'bg-orange-50 text-orange-900/70'}`}
                        >
                            {regular ? t('regular_schedule') : t('private')}
                        </div>

                        {is_nightly && (
                            <div className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full font-medium text-blue-800 bg-blue-100`}>
                                <IconMoon size={12} stroke={2.5} />
                            </div>
                        )}
                        <div className="text-slate-800 font-medium bg-gray-100 rounded-full px-2 py-0.5 flex items-center gap-1">
                            <IconUsers size={10} color="#9ca3af" stroke={2} /> {service.min_pax_per_booking || '0'} <span className="text-gray-400">-</span> {service.max_pax_per_booking || '∞'}
                        </div>
                        <div className="text-slate-500 font-light bg-gray-100 rounded-full px-2 py-0.5">
                            #{service.id}
                        </div>
                    </div>
                </div>, links ? `${SERVICES_PATH}/${service.id}` : null)}
        </div>
    )
}